import { useEffect } from "react";
import * as Slider from "@radix-ui/react-slider";
import type { Question } from "../../lib/survey-config";
import type { QuestionProps } from "./types";
import { QuestionHeader } from "./QuestionHeader";

type SliderQuestionT = Extract<Question, { type: "slider" }>;

function isValid(q: SliderQuestionT, v: number): boolean {
  if (q.required === false) return true;
  return Number.isFinite(v) && v >= q.min && v <= q.max;
}

export function SliderQuestion({
  question,
  value,
  onChange,
  onValid,
  autoFocus,
}: QuestionProps<SliderQuestionT, number>) {
  useEffect(() => {
    onValid(isValid(question, value));
  }, [question, value, onValid]);

  return (
    <div className="space-y-6">
      <QuestionHeader
        title={question.title}
        subtitle={question.subtitle}
        required={question.required !== false}
      />
      <div className="space-y-4">
        <div className="flex items-baseline justify-between">
          <span className="text-xs text-fg-muted">{question.minLabel}</span>
          <span className="text-3xl font-semibold tabular-nums text-accent">
            {value}
          </span>
          <span className="text-xs text-fg-muted">{question.maxLabel}</span>
        </div>
        <Slider.Root
          value={[value]}
          min={question.min}
          max={question.max}
          step={question.step ?? 1}
          onValueChange={(arr) => {
            if (arr[0] != null) onChange(arr[0]);
          }}
          aria-label={question.title}
          className="relative flex h-6 w-full touch-none items-center select-none"
        >
          <Slider.Track className="relative h-1.5 w-full grow rounded-full bg-border">
            <Slider.Range className="absolute h-full rounded-full bg-accent" />
          </Slider.Track>
          <Slider.Thumb
            autoFocus={autoFocus}
            className="block h-6 w-6 rounded-full border-2 border-accent bg-bg-elevated shadow-md transition focus-visible:outline-none"
          />
        </Slider.Root>
        <div className="flex justify-between text-xs text-fg-muted">
          <span>{question.min}</span>
          <span>{question.max}</span>
        </div>
      </div>
    </div>
  );
}
